import { BaseDeviceWrapper } from './BaseDeviceWrapper';
import image from 'images/devices/powerShot-promotion.png';
import { wait } from '../../../utils/wait';

const serviceUuid = '00001400-0000-1000-8000-00805f9b34fb';
// Per firmware doc: control/write commands over TOUCH_CONTROL_UUID (0x1801)
const TOUCH_CONTROL_UUID = 0x1801;
const BATTERY_LEVEL_UUID = 0x2a19;
const SERIAL_NUMBER_CHAR_UUID = 0x2a25;
const AMBIENT_MOVEMENT_KICK_IN_TIMEOUT: number = 2000; // ms
const CYCLE_PERIOD_MS: number = 1000; // Option A: full cycle duration

const names = ['PowerShot'];

export default class PowerShot extends BaseDeviceWrapper {
  private ambientIntensity: number;
  private isVibratingAmbiently: boolean;
  private ambientMovementTimestamp: number;
  private maxIntensity: number;

  constructor(device) {
    // Use TOUCH_CONTROL for primary writes, and battery level as statusChar
    super(device, serviceUuid, TOUCH_CONTROL_UUID, image, BATTERY_LEVEL_UUID);
    this.ambientIntensity = 0;
    this.isVibratingAmbiently = false;
    this.ambientMovementTimestamp = performance.now() + AMBIENT_MOVEMENT_KICK_IN_TIMEOUT;
    this.maxIntensity = 100;
  }

  static get deviceNames() {
    return names;
  }

  /**
   * Needed to request access to these services before connecting to the device
   */
  static get services() {
    return [serviceUuid];
  }

  get companyName() {
    return 'Kiiroo';
  }

  async connect() {
    await super.connect();

    // Identifies this exact toy even after the browser has reset its device id
    await this.readSerialNumber(SERIAL_NUMBER_CHAR_UUID);
  }

  async disconnect() {
    await super.disconnect();
  }

  private async writeCommand(data: Uint8Array) {
    try {
      await this.motorChar.writeValue(data);
    } catch (e) {
      try {
        await this.motorChar.writeValueWithoutResponse(data);
      } catch (err) {
        console.error('PowerShot.writeCommand error:', err);
        throw err;
      }
    }
  }

  async writeToMotorChar(intensity) {
    // BLE command packet per doc: [mode, min1, min2, max1, max2, periodDiv50]
    // We'll use Continuous mode (0x01) where only Max Speeds are used; others set to 0x00.
    // Map intensity [0..100] to max speed for both motors.
    const clamped = Math.max(0, Math.min(100, Math.round(intensity)));
    if (clamped <= 0) {
      // Stop command: send single 0x00 (as per RN implementation and firmware note)

      const stop = new Uint8Array([0x00]);
      await this.writeCommand(stop);
      return;
    }
    const maxSpeed = clamped; // percentage 0..100

    const data = new Uint8Array([0x01, 0x00, 0x00, maxSpeed, maxSpeed, 0x0a]);
    await this.writeCommand(data);
  }

  async write(percent) {
    // Amplitude control only: scale by maxIntensity, independent of ambient duty-cycle
    const maxIntensity = this.maxIntensity;
    const adjustedPercent = (percent * maxIntensity) / 100;
    const intensity = Math.round(Math.min(100, Math.max(0, adjustedPercent)));
    await this.writeToMotorChar(intensity);
  }

  async writePaused() {
    // Send explicit Stop command as single-byte 0x00 twice with a short delay
    const stop = new Uint8Array([0x00]);
    try {
      await this.writeCommand(stop);
    } catch (e) {
      console.error('PowerShot.writePaused stop error:', e);
    } finally {
    }
  }

  async testDevice() {
    await this.writeToMotorChar(this.maxIntensity);
    await wait(2000);
    // Stop: single-byte 0x00 twice with a short delay
    await this.writePaused();
  }

  async getBattery() {
    try {
      if (!this.statusChar) return -1;
      const readData = await this.statusChar.readValue();
      if (readData) {
        return readData.getUint8(0);
      }
    } catch (e) {
      console.error('PowerShot.getBattery:', e);
    }
    return -1;
  }

  async setAmbientMovement(percent: number): Promise<void> {
    this.ambientIntensity = percent;
    if (percent == 0) {
      // 0% duty-cycle -> ensure motor is stopped and reset ambient state
      await this.write(0);
      this.isVibratingAmbiently = false;
    }
    // Restart the ambient timer window so new duty-cycle applies immediately
    this.ambientMovementTimestamp = performance.now();
  }

  /**
   * Sets the maximum intensity.
   * @param percent The percentage intensity to set as the maximum.
   */
  async setMaxIntensity(percent: number): Promise<void> {
    this.maxIntensity = percent;
  }
  async onTimer(): Promise<void> {
    const duty = Math.max(0, Math.min(100, Math.round(this.ambientIntensity)));
    if (duty === 0) {
      if (this.isVibratingAmbiently) {
        await this.write(0);
        this.isVibratingAmbiently = false;
      }
      return;
    }
    const now = performance.now();
    if (now < this.ambientMovementTimestamp) {
      return;
    }

    const onTime = (CYCLE_PERIOD_MS * duty) / 100;
    const offTime = CYCLE_PERIOD_MS - onTime;

    if (this.isVibratingAmbiently) {
      // Switch to OFF phase
      await this.write(0);
      this.ambientMovementTimestamp = now + offTime;
      this.isVibratingAmbiently = false;
    } else {
      // Switch to ON phase at max amplitude (scaled by maxIntensity via write)
      await this.write(100);
      this.ambientMovementTimestamp = now + onTime;
      this.isVibratingAmbiently = true;
    }
  }
}
